import React, { useRef } from 'react';
import {
  Box,
  Container,
  CssBaseline,
  Grid,
  Typography,
} from '@mui/material';
import {
  createTheme,
  ThemeProvider
} from '@mui/material/styles';
import Header from './Header';
import Footer from './Footer';
import FadeInOnScroll from './FadeInOnScroll';
import ContactForm from './ContactForm';
import ScrollToTopButton from './ScrollToTopButton';

const theme = createTheme({
  palette: {
    background: {
      default: '#223f73',
    },
  },
  components:{
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "#f8f6f6",
          color: "#003a66"
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#ffffff'
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#ffffff'
        }
      }
    }
  }
});

function App() {
  const homeRef = useRef(null);
  const companyRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToRef = (ref) => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <ScrollToTopButton />
        <Header scrollToRef={scrollToRef} sectionRefs={{ homeRef, companyRef, contactRef }} />

        <Container  maxWidth="full" disableGutters>
        {/* Section 1 - Title and Subtitle */}
        <Box  ref={homeRef} sx={{ backgroundColor: '#223f73', py: 6, px: 3, textAlign: 'center' }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            PR Innova
          </Typography>
          <Typography variant="h5">
            Innovative Technology Solutions That Simplify Business
          </Typography>
        </Box>
        {/* Section 2 - Company Description */}
        <FadeInOnScroll>
            <Box ref={companyRef} sx={{ backgroundColor: '#2d4f90', py: 5, px: 3 }}>
              <Typography variant="body1" paragraph>
                PR Innova is a technology solutions company created in Puerto Rico with puertorican talent dedicated to
                helping businesses improve efficiency, reduce operational costs, and deliver exceptional customer experiences. 
                We specialize in developing intuitive, user-friendly software that simplifies complex processes — so easy to
                use, even non-tech-savvy users can navigate with confidence.
              </Typography>

              <Typography variant="body1" paragraph>
                Our approach is simple: beautiful design meets powerful functionality. We create visually appealing interfaces
                that are not only modern and elegant, but also practical and easy to use. No learning curve, no frustration —
                just smart, clean technology that works.
              </Typography>

              <Typography variant="body1" paragraph>
                At PR Innova, we don’t just build software — we build complete, scalable solutions that grow with your
                business. Our team listens to real user feedback and continuously improves our products with meaningful
                updates and new features that meet your evolving needs.
              </Typography>
            </Box>
          </FadeInOnScroll>
        {/* Section 3 - Reasons to Choose Us */}
        <FadeInOnScroll>
          <Box sx={{ backgroundColor: '#3a5faa', color: '#fff', py: 5, px: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Why Choose PR Innova?
            </Typography>
            <Grid container spacing={2}>
              {[
                'Custom end-to-end technology solutions',
                'Clean, intuitive UI and UX design',
                'Scalable platforms that evolve with your business',
                'Focus on process automation, cost reduction, and customer satisfaction',
                'Agile development based on user feedback and innovation',
              ].map((point, index) => (
                <Grid key={index}>
                  <Typography variant="body1">• {point}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </FadeInOnScroll>

        {/* Section 4 - Contact Us CTA */}
        <FadeInOnScroll>
          <Box ref={contactRef} sx={{ backgroundColor: '#4b72c2', color: '#fff', py: 5, px: 3, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              PR Innova – Simple. Powerful. Made for people.
            </Typography>
            <Typography variant="body1">
              Get in touch today to discover how we can help your business grow through smarter technology.
            </Typography>
          </Box>
          <ContactForm/>
        </FadeInOnScroll>
        </Container>
        <Footer />
    </ThemeProvider>
  );
}

export default App;
